import type { ApiErrorDetail } from './types.ts'

/**
 * FastAPI schema failures use an array-valued `detail`. A missing structured
 * detail usually means the request reached a static host, proxy, or a backend
 * that does not expose this route. Keeping those cases separate prevents an
 * entire unavailable backend from being misreported as a DSL validation bug.
 */
export function normalizeApiErrorDetail(
  status: number,
  payload: unknown,
  responseTraceId?: string | null,
  requestTraceId?: string
): ApiErrorDetail {
  const envelope = isRecord(payload) ? payload : null
  const rawDetail = envelope?.detail
  const fallbackTraceId = responseTraceId || requestTraceId

  if (isRecord(rawDetail)) {
    const details = rawDetail.details
    const nestedLatest = isRecord(details) ? details.latest : undefined
    return {
      code: stringValue(rawDetail.code) || 'API_ERROR',
      message: stringValue(rawDetail.message) || `请求失败 (${status})`,
      details,
      latest: rawDetail.latest ?? nestedLatest,
      trace_id: stringValue(rawDetail.trace_id) || fallbackTraceId
    }
  }

  if (Array.isArray(rawDetail) || status === 422) {
    return {
      code: 'VALIDATION_ERROR',
      message: `请求校验失败 (${status})`,
      details: rawDetail,
      trace_id: fallbackTraceId
    }
  }

  if (status === 404 || status === 405) {
    return {
      code: 'BACKEND_UNAVAILABLE',
      message: `服务端接口未部署或不可用 (${status})`,
      trace_id: fallbackTraceId
    }
  }

  return {
    code: 'HTTP_ERROR',
    message: `请求失败 (${status})`,
    details: rawDetail,
    trace_id: fallbackTraceId
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringValue(value: unknown) {
  return typeof value === 'string' && value ? value : undefined
}
